import { api } from './api';

/**
 * Conversation Engine — web client. Mirrors the server domain object
 * (apps/api/src/lib/conversation-engine.ts): a Matter has many purpose-typed
 * Conversations, each carrying ConversationMessages with per-message privilege. The
 * server projects every producer (email/portal/notes/conference) and scopes to the
 * caller's privilege tier — the web just renders what it's given.
 */
export type ConversationPurpose =
  | 'instruction' | 'conference' | 'disclosure' | 'settlement' | 'hearing_preparation' | 'court_operations'
  | 'judgment' | 'appeal' | 'general_advice' | 'billing' | 'administration' | 'general';

export type CommunicationChannel =
  | 'in_app' | 'email' | 'portal' | 'conference' | 'note' | 'ai_draft' | 'sms' | 'whatsapp';

export type ConversationPrivilege = 'privileged' | 'operational' | 'external';

export interface ConversationMessage {
  id: string;
  conversationId: string | null;
  channel: CommunicationChannel;
  direction: 'inbound' | 'outbound' | 'internal';
  privilege: ConversationPrivilege;
  from: string | null;
  to: string[];
  subject: string | null;
  body: string;
  sentAt: string;
  source: { producer: string; nativeId: string };
  attachmentIds?: string[];
}

export interface MatterConversation {
  id: string;
  matterId: string | null;
  purpose: ConversationPurpose;
  status: 'open' | 'closed';
  outcome: string | null;
  anchor: { type: string; id: string } | null;
  participants: string[];
  createdAt: string;
  closedAt: string | null;
  messages: ConversationMessage[];
}

export const PURPOSE_LABELS: Record<ConversationPurpose, string> = {
  instruction: 'Instruction',
  conference: 'Conference',
  disclosure: 'Disclosure',
  settlement: 'Settlement',
  hearing_preparation: 'Hearing Preparation',
  court_operations: 'Court Change',
  judgment: 'Judgment',
  appeal: 'Appeal',
  general_advice: 'General Advice',
  billing: 'Billing',
  administration: 'Administration',
  general: 'General',
};

export const CHANNEL_LABELS: Record<CommunicationChannel, string> = {
  in_app: 'Message', email: 'Email', portal: 'Portal', conference: 'Conference',
  note: 'Note', ai_draft: 'AI draft', sms: 'SMS', whatsapp: 'WhatsApp',
};

/** Every conversation on a matter, unified by professional objective. */
export async function fetchMatterConversations(matterId: string): Promise<MatterConversation[]> {
  const res = await api.get<{ data: MatterConversation[] }>(`/matters/${matterId}/conversations`);
  return res.data.data;
}

export type ConversationState = 'waiting_on_me' | 'waiting_on_them' | 'blocked' | 'resolved' | 'dormant';
export type WaitingParty = 'me' | 'them' | 'internal' | 'nobody';
export type ConversationDisposition =
  | 'needs_decision' | 'needs_reply' | 'needs_review' | 'needs_money'
  | 'needs_filing' | 'needs_scheduling' | 'to_do';

/** A conversation that currently REQUIRES me — the Daily-OS unit. Every field is derived
 *  server-side by the Conversation Engine so the Work Queue renders, never computes. */
export interface ActionableConversation {
  conversationId: string;
  matterId: string | null;
  matterName: string | null;
  matterNumber: string | null;
  purpose: ConversationPurpose;
  status: 'open' | 'closed';
  state: ConversationState;
  waitingOn: WaitingParty;
  lastActivityAt: string | null;
  participants: number;
  preview: string;
  disposition: ConversationDisposition;
  dispositionLabel: string;
  action: string;
  attentionScore: number;
  /** The Judgment Engine's reasons behind the score — render the same WHY everywhere. */
  reasons: Array<{ factor: string; contribution: number }>;
  owner: string | null;
  authority: string;
  advance: { needs: string; available: boolean };
}

export interface AdvanceResult {
  conversationId: string;
  state: ConversationState;
  requiresMe: boolean;
  command: string;
}

/** "What conversations currently require me?" across every matter — only actionable ones,
 *  server-ordered by the one Professional Judgment Engine (highest judgment first). */
export async function fetchMyConversations(): Promise<ActionableConversation[]> {
  const res = await api.get<{ data: ActionableConversation[] }>('/me/conversations');
  return res.data.data;
}

/** The single "Start here" — the highest-judged conversation. BOTH the Morning Brief and the
 *  Work Queue derive their first action from this, so they can never disagree. The feed is
 *  already judgment-ordered, so this is simply the head (defensively re-checked). */
export function firstAction(conversations: ActionableConversation[]): ActionableConversation | null {
  if (conversations.length === 0) return null;
  return conversations.reduce((top, c) => (c.attentionScore > top.attentionScore ? c : top), conversations[0]!);
}

/** Advance a conversation via the Command Bus. Resolves to the NEW state; it leaves the
 *  queue only when requiresMe flips false. Throws 409 if the card was stale. */
export async function advanceConversation(conversationId: string, payload: unknown = {}): Promise<AdvanceResult> {
  const res = await api.post<{ data: AdvanceResult }>(`/me/conversations/${conversationId}/advance`, payload);
  return res.data.data;
}

/** One chronological communication entry (mirror of the server conversation-timeline). */
export interface ConversationTimelineEntry {
  message: ConversationMessage;
  purpose: ConversationPurpose;
  conversationId: string;
}

/** Flatten conversations into a single chronological stream (newest first), each entry
 *  still tagged with the objective it belonged to — the "Timeline consumes Conversations"
 *  view. Same shape the server conversation-timeline engine produces for Search/AI. */
export function conversationsToChronology(conversations: MatterConversation[]): ConversationTimelineEntry[] {
  return conversations
    .flatMap((c) => c.messages.map((message) => ({ message, purpose: c.purpose, conversationId: c.id })))
    .sort((a, b) => b.message.sentAt.localeCompare(a.message.sentAt));
}

/** One search across every channel of a matter (mirror of the server conversation-search:
 *  AND semantics, subject-weighted). Fixes "search email in one place, messages in another". */
export function searchConversationsClient(
  conversations: MatterConversation[], query: string,
): ConversationTimelineEntry[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const count = (h: string, t: string) => h.split(t).length - 1;
  const scored: Array<ConversationTimelineEntry & { _score: number }> = [];
  for (const c of conversations) {
    for (const message of c.messages) {
      const subject = (message.subject ?? '').toLowerCase();
      const body = message.body.toLowerCase();
      let score = 0;
      const allPresent = terms.every((term) => {
        const s = count(subject, term), b = count(body, term);
        if (s === 0 && b === 0) return false;
        score += s * 3 + b;
        return true;
      });
      if (allPresent && score > 0) scored.push({ message, purpose: c.purpose, conversationId: c.id, _score: score });
    }
  }
  return scored
    .sort((a, b) => b._score - a._score || b.message.sentAt.localeCompare(a.message.sentAt))
    .map(({ _score, ...e }) => e);
}
